import SpineWrapper, { ICancelablePromise } from '@Core/Animation/SpineWrapper';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { GameScene } from '@Core/GameScene/GameScene';
import AssetsLoadManager from '@Core/Loader/AssetsLoadManager';
import ReelSymbol from '@Core/Reels/ReelSymbol';
import { EWinPresentationStates } from '@Core/Win/States/WinPresentationStates';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EntryCondition } from '@Managers/StateMachine/EntryCondition';
import { LeaveCondition } from '@Managers/StateMachine/LeaveCondition';
import { StateMachine } from '@Managers/StateMachine/StateMachine';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateManager } from '@Managers/StateManager/StateManager';
import { DeferredPromise } from '@Tools/DeferredPromise';
import { EWildWheelFeatureEvents } from './States/WildWheelFeatureEvents';
import { WildWheelFeatureStateConditions } from './States/WildWheelFeatureStateConditions';
import { EWildWheelFeatureStates } from './States/WildWheelFeatureStates';
import { WildWheelFeatureStatesDefaults } from './States/WildWheelFeatureStatesDefaults';
import { WildWheelInstance } from './WildWheelInstance';
import { WildWheelMerge } from './WildWheelMerge';

export class WildWheelFeature extends GameComponent {
    public scene: GameScene;
    public wildWheelFeatureStateMachine: StateMachine<EWildWheelFeatureStates>;
    private entryCondition: EntryCondition<EGameStates>;
    private leaveCondition: LeaveCondition<EGameStates>;
    private wildWheelInstances: WildWheelInstance[] = [];
    private wildWheelMerge: WildWheelMerge;
    private skipPromise: DeferredPromise;
    private endPromise: DeferredPromise;
    private wheelCount = 1;
    private aboveReelsSpines: SpineWrapper[] = [];
    private wilds: ReelSymbol[] = [];
    private isBonusWheel: boolean = false;

    constructor(scene: GameScene) {
        super(scene);
        this.scene = scene;
        Logger.log(LoggerLevel.Constructor, { class: WildWheelFeature.name, source: LoggerSource.Game, message: 'WildWheelFeature constructor' });
        EventsCenter.on(EGameStates.WILD_WHEEL_STATE, this.onFeatureStart, this);
        EventsCenter.on(EGameStates.COMPLETE_WIN_STATE, this.onCompleteWin, this);
        EventsCenter.on(EWinPresentationStates.POST_WIN_PRESENTATION_STATE, this.onCompleteWinPresentation, this);
        EventsCenter.on(EGameEvents.TAKE, this.onCompleteWinPresentation, this);
        EventsCenter.on(EGameEvents.SKIP, this.onSkip, this);
        EventsCenter.on(EGameStates.COMPLETE_SPIN_STATE, this.onReelSpinEnd, this);
    }

    public onCreate(): void {
        this.skipPromise = new DeferredPromise();
        this.endPromise = new DeferredPromise();
        this.leaveCondition = new LeaveCondition('waitForMultiplierFeature');
        this.entryCondition = new EntryCondition('checkMultiplierFeature', () => {
            const step = this.gameStore.get(EGameStoreKeys.SpinOutcome).steps[0];
            const isWildWheel = !!(step.totalWinMultiplier && this.gameStore.get(EGameStoreKeys.SpinOutcome).win > 0);
            const isBonusWheel = !!(step.bonusWheelMultiplier && this.gameStore.get(EGameStoreKeys.SpinOutcome).win > 0);
            return isWildWheel || isBonusWheel;
        });

        const subStates = {
            PRE_WILD_WHEEL_STATE: {
                entryConditions: [this.entryCondition],
                leaveConditions: []
            },
            WILD_WHEEL_STATE: {
                entryConditions: [this.entryCondition],
                leaveConditions: [this.leaveCondition]
            },
            POST_WILD_WHEEL_STATE: {
                entryConditions: [this.entryCondition],
                leaveConditions: []
            }
        };

        StateManager.assignSubStates(EGameStates.PRE_WIN_FEATURES_STATE, subStates);

        const { wheels, merge } = this.gameStore.get(EGameStoreKeys.WildWheelConfiguration);
        this.wheelCount = wheels.length;

        if (merge?.spineName) {
            this.wildWheelMerge = new WildWheelMerge(this.scene, this);
        }

        for (let i = 0; i < this.wheelCount; i++) {
            this.wildWheelInstances.push(new WildWheelInstance(this.scene, this, i));
        }

        this.scene.aboveReels?.spine.eventManager.registerBlocker('ensureWinWithMultiplier', this.blockAboveReels.bind(this));
        this.createStateMachine();
        this.createAboveReels();
        this.wilds = this.getWilds();
    }

    private createAboveReels(): void {
        const declaredAssets = AssetsLoadManager.getSpinesConfiguration().assets;

        declaredAssets
            .filter((asset) => asset.key.includes('aboveReelsWildWheel'))
            .forEach((asset) => {
                const spine = new SpineWrapper(
                    this.scene,
                    {
                        x: this.scene.screen.width / 2,
                        y: this.scene.screen.height / 2
                    },
                    { dataKey: asset.key, skinName: 'default' },
                    true
                );

                spine.spine.zIndex = ELayeringOrder.ABOVE_REELS;
                spine.spine.visible = false;
                this.aboveReelsSpines.push(spine);
            });
    }

    private createStateMachine(): void {
        this.wildWheelFeatureStateMachine = new StateMachine<EWildWheelFeatureStates>(WildWheelFeatureStatesDefaults, WildWheelFeatureStateConditions, {
            onStateChange: (state) => {
                if (state === EWildWheelFeatureStates.WILD_WHEEL_HIDDEN) {
                    for (const wwInstance of this.wildWheelInstances) {
                        if (!wwInstance.spine.spine.visible) wwInstance.spine.spine.visible = true;
                    }

                    return;
                }

                if (state === EWildWheelFeatureStates.WILD_WHEEL_END && this.wilds.length < 2) {
                    for (let i = 0; i < this.aboveReelsSpines.length; i++) {
                        this.aboveReelsSpines[i].spine.visible = !!this.getWildForInstance(this.wildWheelInstances[i].assignedReelIndex);
                    }
                }
                const step = this.gameStore.get(EGameStoreKeys.SpinOutcome).steps[0];
                this.isBonusWheel = !!step.bonusWheelMultiplier;
                this.onCurrentStep();
            }
        });
        this.wildWheelFeatureStateMachine.setGameState(EWildWheelFeatureStates.WILD_WHEEL_HIDDEN);
    }

    private async onCurrentStep(): Promise<void> {
        this.skipPromise.reset();
        const currentState = this.wildWheelFeatureStateMachine.getCurrentState();
        const wildCount = this.wilds.length;
        const animationPromises: ICancelablePromise[] = [];
        const isMergeStart = currentState === EWildWheelFeatureStates.WILD_WHEEL_MERGE_START;
        const isEnd = currentState === EWildWheelFeatureStates.WILD_WHEEL_END;

        if (this.wheelCount > 1) animationPromises.push(...this.handleWildCardEvent(currentState));

        if (isMergeStart) {
            for (let i = 0; i < wildCount; i++) {
                this.wildWheelInstances[i].spine.spine.visible = false;
            }

            animationPromises.push(this.wildWheelMerge.spine.waitForAnimationEvent(EWildWheelFeatureStates.WILD_WHEEL_MERGE_START, 'complete'));
        }

        if (currentState === EWildWheelFeatureStates.WILD_WHEEL_MERGE_END) {
            animationPromises.push(this.wildWheelMerge.spine.waitForAnimationEvent(EWildWheelFeatureStates.WILD_WHEEL_MERGE_END, 'complete'));
        } else if (this.wheelCount < 2 && wildCount < 2 && !this.isBonusWheel) {
            for (let i = 0; i < wildCount; i++) {
                const wwInstance = this.wildWheelInstances.find((wwInstance) => wwInstance.assignedReelIndex === this.wilds[i].reelIndex)!;
                animationPromises.push(wwInstance.spine.waitForAnimationEvent(currentState, 'complete'));
            }
        } else if (this.isBonusWheel) {
            const wwInstance = this.wildWheelInstances[0];
            animationPromises.push(wwInstance.spine.waitForAnimationEvent(currentState, 'complete'));
        }

        await Promise.race([Promise.all(animationPromises.map((animationPromise) => animationPromise.promise)), this.skipPromise.promise]);

        animationPromises.forEach((animationPromise) => animationPromise.cancel());

        if (isMergeStart) {
            this.endPromise.reset();
            this.leaveCondition.resolve();
            await this.endPromise.promise;
        } else if (isEnd) {
            this.leaveCondition.resolve();
        }

        this.wildWheelFeatureStateMachine.getState(currentState)?.leaveConditions?.forEach((lc) => lc.resolve());
    }

    private handleWildCardEvent(currentState: EWildWheelFeatureStates): ICancelablePromise[] {
        const resultPromises: ICancelablePromise[] = [];

        const stateConfig: Partial<
            Record<
                EWildWheelFeatureStates,
                {
                    event: EWildWheelFeatureEvents;
                    waitForAll: boolean;
                }
            >
        > = {
            [EWildWheelFeatureStates.WILD_WHEEL_START]: {
                event: EWildWheelFeatureEvents.WILD_WHEEL_START_$,
                waitForAll: false
            },
            [EWildWheelFeatureStates.WILD_WHEEL_SPIN]: {
                event: EWildWheelFeatureEvents.WILD_WHEEL_SPIN_$,
                waitForAll: false
            },
            [EWildWheelFeatureStates.WILD_WHEEL_RESULT]: {
                event: EWildWheelFeatureEvents.WILD_WHEEL_RESULT_$,
                waitForAll: true
            },
            [EWildWheelFeatureStates.WILD_WHEEL_END]: {
                event: EWildWheelFeatureEvents.WILD_WHEEL_END_$,
                waitForAll: true
            }
        };

        const config = stateConfig[currentState];
        if (!config) return resultPromises;

        this.wilds.forEach((wild, index) => {
            const reelIndex = wild.reelIndex;
            const wwInstance = this.wildWheelInstances.find((ww) => ww.assignedReelIndex === reelIndex);
            const delay = wwInstance?.wildWheelConfig.delay;
            if (!wwInstance) return;

            if (delay && this.wilds.length > 1) {
                this.scene.time.delayedCall(delay, () => {
                    EventsCenter.emit(config.event, { eventIds: [reelIndex + 1] });
                });
            } else {
                EventsCenter.emit(config.event, { eventIds: [reelIndex + 1] });
            }

            if (config.waitForAll || index === 0) {
                resultPromises.push(wwInstance.spine.waitForAnimationEvent(config.event, 'complete', [reelIndex + 1]));
            }
        });

        return resultPromises;
    }

    private onCompleteWin(): void {
        if (this.isBonusWheel) return;
        this.wilds.forEach((wild, index) => {
            if (wild) {
                this.wildWheelInstances.forEach((instance) => instance.applyMultiplier(wild.symbolAnimation, 2));
            }

            if (this.aboveReelsSpines.length > 0) {
                for (const element of this.aboveReelsSpines) {
                    element.spine.visible = false;
                }
            }
        });
    }

    private onCompleteWinPresentation(): void {
        if (this.isInWildWheelMergingSequence()) this.endPromise.resolve();
    }

    private onFeatureStart(): void {
        WildWheelFeatureStateConditions.hiddenCondition.resolve();
        this.wilds = this.getWilds();
        this.skipPromise.reset();
    }

    private onReelSpinEnd(): void {
        this.wilds = [];
    }

    private onSkip() {
        if (this.gameStore.get(EGameStoreKeys.CurrentGameSubState) !== EGameStates.WILD_WHEEL_STATE) return;
        if (!this.gameStore.get(EGameStoreKeys.WildWheelConfiguration).skippable) return;
        this.skipPromise.resolve();
    }

    public getWilds(): ReelSymbol[] {
        const visibleSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);
        const wilds = [];
        for (const reel of visibleSymbols) {
            for (const symbol of reel) {
                if (symbol.isWild()) wilds.push(symbol);
            }
        }
        return wilds;
    }

    public getWildForInstance(assignedReelIndex: number): ReelSymbol | undefined {
        return this.wilds.find((wild) => wild.reelIndex === assignedReelIndex);
    }

    public getMultiplierForInstance(instanceIndex: number): number | undefined {
        const step = this.gameStore.get(EGameStoreKeys.SpinOutcome).steps[0];
        const multipliers = step.winMultipliers;
        if (multipliers && multipliers.length > 1) return multipliers[instanceIndex];
        if (step.bonusWheelMultiplier) return step.bonusWheelMultiplier;
        return step.winMultiplier;
    }

    public isWinStateWithoutWheel(): boolean {
        const isWinWithWheel =
            this.gameStore.get(EGameStoreKeys.CurrentGameState) === EGameStates.WIN_STATE &&
            !this.gameStore.get(EGameStoreKeys.SpinOutcome).steps[0].totalWinMultiplier &&
            !this.gameStore.get(EGameStoreKeys.SpinOutcome).steps[0].bonusWheelMultiplier;

        return isWinWithWheel;
    }

    public isInWildWheelSequence(): boolean {
        return this.wildWheelFeatureStateMachine.getCurrentState() !== EWildWheelFeatureStates.WILD_WHEEL_HIDDEN;
    }

    public isInWildWheelMergingSequence(): boolean {
        return this.isInWildWheelSequence() && this.wilds.length > 1;
    }

    public blockAboveReels(): boolean {
        if (this.wildWheelMerge) {
            return this.wilds.length < 2;
        }

        return (
            this.gameStore.get(EGameStoreKeys.CurrentGameState) === EGameStates.WIN_STATE &&
            !this.gameStore.get(EGameStoreKeys.SpinOutcome).steps[0].totalWinMultiplier
        );
    }
}
